import React, { useContext } from "react";
import { Users, UserCheck, UserX, Palmtree, DollarSign } from "lucide-react";
import { ThemeContext } from "../../../../constant/theme";
import { useAuth } from "../../../../context/AuthContext";
import { SkeletonStyles, SkeletonStatCard } from "../../../../components/SkeletonLoader";
import SharedStatsGrid from "../../../../components/StatsGrid";
import { getResponsiveStyles } from "../../../../utils/styleUtils";

const ICONS = {
  totalEmployees: Users,
  activeEmployees: UserCheck,
  absentToday: UserX,
  onLeave: Palmtree,
  payrollCostThisMonth: DollarSign,
};

const COLORS = {
  totalEmployees: "#3b82f6",
  activeEmployees: "#10b981",
  absentToday: "#ef4444",
  onLeave: "#f59e0b",
  payrollCostThisMonth: "#06b6d4",
};

const formatCurrency = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toFixed(0);
};

// Fluid 5-up row on desktop, wraps on tablets/phones — same grid the
// Employee Management pages use for their five stat cards.
const GRID_STYLE = {
  gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))",
};

// Renders the Reports KPIs through the app-wide StatsGrid
// (src/components/StatsGrid.jsx) so they share the exact design used by
// Dashboard / Employee Management. Only the data is page-specific.
const KpiCardsRow = ({ kpis, kpisLoading, text, language, screenSize }) => {
  const { theme } = useContext(ThemeContext);
  const { isFeatureVisible } = useAuth();
  const styles = getResponsiveStyles(screenSize, theme, language);

  if (kpisLoading && !kpis) {
    return (
      <div style={{ ...styles.statsGrid, ...GRID_STYLE }}>
        <SkeletonStyles theme={theme} />
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonStatCard key={i} cardStyle={styles.statCard} />
        ))}
      </div>
    );
  }

  const cards = kpis ? [
    { key: "totalEmployees", label: text[language].kpiTotalEmployees, value: kpis.totalEmployees, subtitle: text[language].kpiSubtitleAllStates },
    { key: "activeEmployees", label: text[language].kpiActiveEmployees, value: kpis.activeEmployees, subtitle: text[language].kpiSubtitleCurrentlyActive },
    { key: "absentToday", label: text[language].kpiAbsentToday, value: kpis.absentToday, subtitle: text[language].kpiSubtitleToday },
    { key: "onLeave", label: text[language].kpiOnLeave, value: kpis.onLeave, subtitle: text[language].kpiSubtitleToday },
    { key: "payrollCostThisMonth", label: text[language].kpiPayrollCost, value: formatCurrency(kpis.payrollCostThisMonth), subtitle: text[language].kpiSubtitleMonthToDate },
  ].filter((c) => isFeatureVisible?.("reports", `kpi_${c.key}`) !== false) : [];

  const stats = cards.map((card) => ({
    title: card.label,
    value: card.value,
    subtitle: card.subtitle,
    icon: ICONS[card.key],
    iconBackground: COLORS[card.key],
    showSymbol: true,
    valueAlign: 50,
  }));

  return (
    <SharedStatsGrid
      styles={styles}
      stats={stats}
      screenSize={screenSize}
      gridStyle={GRID_STYLE}
    />
  );
};

export default KpiCardsRow;
